import { LearningDesign } from '../../Types/SectionTemplates';
import { ExperienceResourceArray } from '../../Types';
import FilterBar from './Filters';
import SectionCard from './SectionCard';
import { CompetencyFilterOptionData, ProductTypeFilterOptionData } from './Filters/Utils';

// Helper function to get unique count from array
const getUniqueCount = <T,>(items: T[]): number => new Set(items).size;

// Helper to derive section metadata from experiences
const deriveSectionMetadata = (
  experienceIds: number[],
  allExperiences: ExperienceResourceArray,
): { programsCount: number; schoolPartnersCount: number; competenciesCount: number } => {
  const schoolPartners: string[] = [];
  const competencyNames: string[] = [];

  experienceIds.forEach((expId) => {
    const experience = allExperiences.find((e) => e.payload.id === expId);
    if (experience) {
      if (experience.payload.school_partner_name) {
        schoolPartners.push(experience.payload.school_partner_name);
      }
      if (experience.payload.competency_names) {
        competencyNames.push(...experience.payload.competency_names);
      }
    }
  });

  return {
    programsCount: experienceIds.length,
    schoolPartnersCount: getUniqueCount(schoolPartners),
    competenciesCount: getUniqueCount(competencyNames),
  };
};

interface Props {
  filteredSections: LearningDesign[];
  experiencesArray: ExperienceResourceArray;
  selectedIds: number[];
  onToggleSelection: (id: number) => void;
  onViewDetail: (id: number) => void;
  competencyFilterOptions: CompetencyFilterOptionData[];
  productTypeFilterOptions: ProductTypeFilterOptionData[];
  selectedCompetencies: string[];
  selectedProductTypes: string[];
  onCompetenciesChange: (values: string[]) => void;
  onProductTypesChange: (values: string[]) => void;
}

const ListView: React.FC<Props> = ({
  filteredSections,
  experiencesArray,
  selectedIds,
  onToggleSelection,
  onViewDetail,
  competencyFilterOptions,
  productTypeFilterOptions,
  selectedCompetencies,
  selectedProductTypes,
  onCompetenciesChange,
  onProductTypesChange,
}) => (
  <div
    className="flex flex-col flex-1 space-y-4 overflow-hidden"
    data-intercom-target="curated-sections-list-view"
  >
    <FilterBar
      competencyOptions={competencyFilterOptions}
      productTypeOptions={productTypeFilterOptions}
      selectedCompetencies={selectedCompetencies}
      selectedProductTypes={selectedProductTypes}
      onCompetenciesChange={onCompetenciesChange}
      onProductTypesChange={onProductTypesChange}
    />

    <div className="space-y-3 flex-1 overflow-y-auto">
      {filteredSections.map((section) => {
        const metadata = deriveSectionMetadata(section.experiences, experiencesArray);
        return (
          <SectionCard
            key={section.id}
            section={section}
            metadata={metadata}
            isSelected={selectedIds.includes(section.id)}
            onToggleSelection={onToggleSelection}
            onViewDetail={onViewDetail}
          />
        );
      })}
    </div>
  </div>
);

export default ListView;
